// TemplateCandidatesAdminPage.js — Phase I of TemplatePromotionFromFeedback v1
//
// Admin-only review queue at /admin/template-candidates. Lists `template_candidates`
// docs from Firestore, lets the admin run the discriminator (Anthropic LLM call,
// returns confidence + suggested slug/category/keywords), then promote (commits
// reference.html + template-manifest.json into mchatai-source via GitHub API)
// or reject (logs reason).
//
// Bottom panel offers manual seeding: paste any hub slug → backend looks it up,
// fetches its previewURL HTML, and writes a candidate doc. Useful when the
// auto-capture flow on Deploy/Publish hasn't yet produced organic candidates,
// or for surfacing already-popular hub apps that should become templates.
//
// Auth: same UID allowlist + custom-claim pattern as AdminSecurityPage.
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { fetchPromotedTemplates, indexBySlug } from './lib/mchataiSourceTemplatesAPI';
import './App.css';

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'];

// Suggested hub-app slugs that the gap-analysis identified as missing from the
// current template catalog. Admin can one-click seed any of these into the
// review queue. Ordered tier A first (clear distinct genre, classic, ≥4.0
// rating), tier B second (more niche). Ratings reflect a small-sample read of
// /v1/ratings/summary-bulk on 2026-04-30.
const SUGGESTED_GAPS = [
  // Web (miniApp) — fetched as previewURL HTML
  { tier: 'A', platform: 'web', slug: 'classic-snake',       label: 'Classic Snake',       why: 'Universal arcade staple — no template yet' },
  { tier: 'A', platform: 'web', slug: 'pong',                label: 'Pong',                why: '2-player arcade canon' },
  { tier: 'A', platform: 'web', slug: 'minesweeper-99',      label: 'Minesweeper 9×9',     why: 'Grid puzzle distinct from 2048' },
  { tier: 'A', platform: 'web', slug: 'tic-tac-toe-vs-ai',   label: 'Tic-Tac-Toe vs AI',   why: 'Beginner board + AI' },
  { tier: 'A', platform: 'web', slug: 'connect-four',        label: 'Connect Four',        why: 'Vertical-drop board' },
  { tier: 'A', platform: 'web', slug: 'calculator',          label: 'Calculator',          why: 'Utility — none in catalog yet' },
  { tier: 'A', platform: 'web', slug: 'playable-chess',      label: 'Playable Chess',      why: 'Board game, distinct from checkers' },
  { tier: 'A', platform: 'web', slug: 'tower-defense',       label: 'Tower Defense',       why: 'Strategy genre, big request' },
  { tier: 'B', platform: 'web', slug: 'mastermind',          label: 'Mastermind',          why: 'Code-breaking puzzle' },
  { tier: 'B', platform: 'web', slug: 'simon-says',          label: 'Simon Says',          why: 'Memory game' },
  { tier: 'B', platform: 'web', slug: 'asteroids',           label: 'Asteroids',           why: 'Space shooter w/ inertia' },
  { tier: 'B', platform: 'web', slug: 'candy-match',         label: 'Candy Match',         why: 'Match-3 puzzle' },
  { tier: 'B', platform: 'web', slug: 'word-quest',          label: 'Word Quest',          why: 'Word puzzle' },
  { tier: 'B', platform: 'web', slug: 'texas-holdem-poker',  label: 'Texas Holdem Poker',  why: 'Card game (poker, not blackjack)' },

  // macOS (macOSApp) — bundleURL .zip extracted to a Swift Package fileMap.
  // 2026-04-30 gap analysis on contentCategory:macOSApp + Lawrence's hand-pick.
  { tier: 'A', platform: 'macos', slug: 'breakout',                  label: 'Breakout',                  why: 'Classic brick-breaker — Lawrence hand-pick' },
  { tier: 'A', platform: 'macos', slug: 'tetris',                    label: 'Tetris',                    why: 'Falling-block puzzle — Lawrence hand-pick' },
  { tier: 'A', platform: 'macos', slug: 'asteroids-macos',           label: 'Asteroids (macOS)',         why: 'Space shooter w/ inertia — Lawrence hand-pick' },
  { tier: 'B', platform: 'macos', slug: 'galaga-swoop',              label: 'Galaga Swoop',              why: 'Wave shooter scaffold' },
  { tier: 'B', platform: 'macos', slug: 'joust',                     label: 'Joust',                     why: 'Versus-flight arcade' },
  { tier: 'B', platform: 'macos', slug: 'othello',                   label: 'Othello',                   why: 'Board game w/ AI' },
  { tier: 'B', platform: 'macos', slug: 'quick-notes',               label: 'Quick Notes',               why: 'Productivity scaffold (not a game)' },
  { tier: 'B', platform: 'macos', slug: 'virtual-pet-macos-menubar', label: 'Virtual Pet (menubar)',     why: 'Menubar-app pattern' },
];

// Cost estimates indexed by MODEL (not provider) — this matters because the
// same provider (openai) can route to wildly different price tiers
// (gpt-4o-mini vs gpt-4o is a ~40x spread). Costs are rough — derived from
// public 2026 pricing for ~15K input + ~150 output tokens (the typical
// discriminator load on a miniApp HTML body). Update these when pricing
// shifts or when policy adds a new model. Lookup is best-effort: any model
// not listed here falls back to the per-PROVIDER default below.
const MODEL_COST_INFO = {
  'gpt-4o-mini': {
    costPerRun: '~$0.001/run',
    perDollar: '~1,250 runs / $1',
    note: 'Cheapest. Note: 2026-04-30 QA found mini false-declines classic genres (Minesweeper, Pong) by reasoning from world knowledge instead of provided context. Only use after re-validating prompt resists this.',
  },
  'gpt-4o': {
    costPerRun: '~$0.04/run',
    perDollar: '~25 runs / $1',
    note: 'Strong reasoning, follows EXISTING_TEMPLATES_BY_CATEGORY correctly. Recommended for the discriminator after gpt-4o-mini hallucination issues.',
  },
  'claude-sonnet-4-6': {
    costPerRun: '~$0.017/run',
    perDollar: '~60 runs / $1',
    note: 'Anthropic mid-tier. ~2x cheaper than gpt-4o, similar reasoning quality. Requires ANTHROPIC_API_KEY credit balance.',
  },
  'claude-haiku-4-5': {
    costPerRun: '~$0.002/run',
    perDollar: '~500 runs / $1',
    note: 'Anthropic cheap tier. Untested on this prompt — may hit the same world-knowledge issue as gpt-4o-mini.',
  }
};

// Per-provider fallback when the model isn't in MODEL_COST_INFO above.
const PROVIDER_FALLBACK_COST_INFO = {
  openai: {
    costPerRun: '~?',
    perDollar: '',
    note: 'OpenAI provider — no cost data for this model; check pricing at openai.com.',
    badge: null
  },
  anthropic: {
    costPerRun: '~?',
    perDollar: '',
    note: 'Anthropic provider — no cost data for this model; check pricing at anthropic.com.',
    badge: null
  },
  google: {
    costPerRun: '~$0.0008/run',
    perDollar: '~1,250 runs / $1',
    note: 'NOT YET WIRED — extend runDiscriminator with a Google branch first.',
    badge: 'Coming soon'
  }
};

function costInfoFor(provider, model) {
  const byModel = MODEL_COST_INFO[model];
  if (byModel) return byModel;
  return PROVIDER_FALLBACK_COST_INFO[provider] || { costPerRun: '~?', perDollar: '', note: '' };
}

// Order the dropdown so the cheapest/recommended is first.
const PROVIDER_DISPLAY_ORDER = ['openai', 'anthropic', 'google'];

// Subset of providers the Cloud Function currently supports. Adding 'google'
// here without wiring the function first would just produce runtime errors.
const SUPPORTED_PROVIDERS = new Set(['openai', 'anthropic']);

const STATUS_FILTERS = [
  { id: 'unscreened', label: 'Unscreened',          predicate: (c) => !c.discriminator && !c.adminDecision },
  { id: 'recommended', label: 'Recommended',        predicate: (c) => c.discriminator?.decision === 'promote' && !c.adminDecision },
  { id: 'declined-by-discriminator', label: 'Declined by discriminator', predicate: (c) => c.discriminator?.decision === 'decline' && !c.adminDecision },
  { id: 'promoted',  label: 'Promoted',             predicate: (c) => c.adminDecision?.kind === 'promoted' },
  { id: 'rejected',  label: 'Rejected',             predicate: (c) => c.adminDecision?.kind === 'rejected' },
  { id: 'all',       label: 'All',                  predicate: () => true },
];

export default function TemplateCandidatesAdminPage() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [statusFilter, setStatusFilter] = useState('unscreened');
  const [selected, setSelected] = useState(null);
  const [actionLog, setActionLog] = useState([]);
  const [busyAction, setBusyAction] = useState(null); // { kind, candidateID }

  // Manual seed form state
  const [seedSlug, setSeedSlug] = useState('');
  const [seedingAll, setSeedingAll] = useState(false);

  // Live promotion policy from mchatai-source — drives the provider dropdown's
  // default + the per-provider model labels. Cached here at page load; the
  // Cloud Function fetches its own copy at invocation time so the UI doesn't
  // have to be perfectly fresh, just close.
  const [policy, setPolicy] = useState(null);
  useEffect(() => {
    fetch('https://raw.githubusercontent.com/LawrenceNorman/mchatai-source/main/harness/template-promotion-policy.json')
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`policy fetch ${r.status}`)))
      .then(setPolicy)
      .catch(err => console.warn('[admin] policy fetch failed:', err.message));
  }, []);

  // Live snapshot of templates currently in mchatai-source — used for
  // collision detection in the Promote form (slug already exists?). Refreshed
  // alongside candidates so post-promote the new slug shows as taken.
  const [existingTemplates, setExistingTemplates] = useState({});
  const reloadExistingTemplates = useCallback(async () => {
    try {
      const list = await fetchPromotedTemplates();
      setExistingTemplates(indexBySlug(list));
    } catch (err) {
      console.warn('[admin] existing-templates fetch failed:', err.message);
    }
  }, []);
  useEffect(() => { reloadExistingTemplates(); }, [reloadExistingTemplates]);

  // Optional URL query param targetSlug=foo&targetSubdir=miniapps from the
  // Templates catalog page's "Replace from candidate" links. When set,
  // candidates default to that slug in the Promote form, making it one
  // click to commit a replacement.
  const [searchParams] = useSearchParams();
  const targetSlugFromURL = searchParams.get('targetSlug') || null;
  const targetSubdirFromURL = searchParams.get('targetSubdir') || null;

  useEffect(() => {
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u && (ADMIN_UIDS.includes(u.uid) || (await u.getIdTokenResult()).claims?.role === 'admin')) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const db = getFirestore();
      // Top-level collection per the Mac app's TemplateCandidateStore (`template_candidates`).
      const q = query(collection(db, 'template_candidates'), orderBy('createdAt', 'desc'), limit(200));
      const snap = await getDocs(q);
      setCandidates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      pushLog('error', `Failed to load candidates: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin, refresh]);

  function pushLog(kind, message) {
    setActionLog(prev => [{ ts: new Date().toLocaleTimeString(), kind, message }, ...prev].slice(0, 20));
  }

  async function callFn(name, payload) {
    const fn = httpsCallable(getFunctions(), name);
    const result = await fn(payload);
    return result.data;
  }

  async function runDiscriminator(c, providerOverride) {
    setBusyAction({ kind: 'discriminator', candidateID: c.candidateID });
    try {
      const payload = { candidateID: c.candidateID };
      if (providerOverride) payload.providerOverride = providerOverride;
      const data = await callFn('runDiscriminator', payload);
      const providerLabel = data.provider ? ` [${data.provider}/${data.model}]` : '';
      pushLog('ok', `Discriminator${providerLabel}: ${data.decision} (${(data.confidence * 100).toFixed(0)}%) — ${data.reasoning?.slice(0, 80) || ''}`);
      await refresh();
    } catch (err) {
      pushLog('error', `runDiscriminator failed: ${err.message}`);
    } finally {
      setBusyAction(null);
    }
  }

  async function promote(c, override = {}) {
    setBusyAction({ kind: 'promote', candidateID: c.candidateID });
    try {
      const data = await callFn('promoteTemplateCandidate', { candidateID: c.candidateID, ...override });
      pushLog('ok', `Promoted as ${data.slug} → ${data.commitURL}`);
      await refresh();
      // Refresh the existing-templates snapshot so the new slug shows as taken
      // in any subsequent Promote form on the same page.
      reloadExistingTemplates();
    } catch (err) {
      pushLog('error', `promote failed: ${err.message}`);
    } finally {
      setBusyAction(null);
    }
  }

  async function reject(c) {
    const reason = window.prompt('Rejection reason (optional):', '') ?? '';
    setBusyAction({ kind: 'reject', candidateID: c.candidateID });
    try {
      await callFn('rejectTemplateCandidate', { candidateID: c.candidateID, reason });
      pushLog('ok', `Rejected ${c.candidateID.slice(0, 8)}`);
      await refresh();
    } catch (err) {
      pushLog('error', `reject failed: ${err.message}`);
    } finally {
      setBusyAction(null);
    }
  }

  async function seedFromSlug(slug, originalGoal) {
    setBusyAction({ kind: 'seed', candidateID: slug });
    try {
      const data = await callFn('seedHubAppAsTemplateCandidate', { slug, originalGoal });
      const sizeNote = data.fileCount
        ? `${data.fileCount} source files, ${data.bytes} bytes`
        : `${data.htmlBytes || '?'} bytes`;
      const typeNote = data.contentCategory ? ` [${data.contentCategory}]` : '';
      pushLog('ok', `Seeded ${slug}${typeNote} as candidate ${data.candidateID.slice(0, 8)} (${sizeNote})`);
      await refresh();
    } catch (err) {
      pushLog('error', `seed ${slug} failed: ${err.message}`);
    } finally {
      setBusyAction(null);
    }
  }

  async function seedTierA(platformFilter = null) {
    setSeedingAll(true);
    const tierA = SUGGESTED_GAPS.filter(g => g.tier === 'A' && (!platformFilter || g.platform === platformFilter));
    for (const g of tierA) {
      try {
        const data = await callFn('seedHubAppAsTemplateCandidate', { slug: g.slug });
        const sizeNote = data.fileCount
          ? `${data.fileCount} files, ${data.bytes} bytes`
          : `${data.htmlBytes || '?'} bytes`;
        pushLog('ok', `Seeded ${g.slug} (${sizeNote})`);
      } catch (err) {
        pushLog('error', `Skip ${g.slug}: ${err.message}`);
      }
    }
    setSeedingAll(false);
    await refresh();
  }

  // Batch operations — both sequential. Promote-all MUST be sequential
  // because parallel commits to mchatai-source main would race on parent
  // SHA in createCommit → updateRef. Run-discrim-all could be parallel
  // (different candidate docs, OpenAI tier-1 has 500 RPM) but sequential
  // keeps the action log readable + bounded LLM cost in case the user
  // hits stop. Each iteration writes a progress line to the action log
  // so the admin can watch it in real time without a separate progress UI.
  const [batchRunning, setBatchRunning] = useState(false);

  async function runDiscriminatorAll() {
    const targets = candidates.filter(c => !c.discriminator && !c.adminDecision);
    if (targets.length === 0) {
      pushLog('ok', 'No unscreened candidates to run.');
      return;
    }
    setBatchRunning(true);
    pushLog('ok', `Batch: running discriminator on ${targets.length} candidate(s)…`);
    let okCount = 0, failCount = 0;
    for (let i = 0; i < targets.length; i++) {
      const t = targets[i];
      try {
        const data = await callFn('runDiscriminator', { candidateID: t.candidateID });
        okCount++;
        pushLog('ok', `[${i + 1}/${targets.length}] ${t.candidateID.slice(0, 8)} ${(t.originalGoal || '').slice(0, 30)} → ${data.decision} (${(data.confidence * 100).toFixed(0)}%)`);
      } catch (err) {
        failCount++;
        pushLog('error', `[${i + 1}/${targets.length}] ${t.candidateID.slice(0, 8)} failed: ${err.message}`);
      }
    }
    pushLog('ok', `Batch done — ${okCount} ok, ${failCount} failed.`);
    setBatchRunning(false);
    await refresh();
  }

  async function promoteAllRecommended() {
    const targets = candidates.filter(c => c.discriminator?.decision === 'promote' && !c.adminDecision);
    if (targets.length === 0) {
      pushLog('ok', 'No discriminator-recommended candidates to promote.');
      return;
    }
    const confirmed = window.confirm(
      `Promote ${targets.length} recommended candidate${targets.length === 1 ? '' : 's'}?\n\n` +
      `Each will commit reference.html + template-manifest.json to mchatai-source main. ` +
      `Sequential — expect ~3-5 seconds per candidate. The discriminator's suggested ` +
      `slug, category, and keywords will be used (same as clicking Promote on each one ` +
      `without overrides).`
    );
    if (!confirmed) return;
    setBatchRunning(true);
    pushLog('ok', `Batch: promoting ${targets.length} candidate(s) sequentially…`);
    let okCount = 0, failCount = 0;
    for (let i = 0; i < targets.length; i++) {
      const t = targets[i];
      try {
        const data = await callFn('promoteTemplateCandidate', { candidateID: t.candidateID });
        okCount++;
        pushLog('ok', `[${i + 1}/${targets.length}] Promoted ${data.slug} → ${data.commitURL}`);
      } catch (err) {
        failCount++;
        pushLog('error', `[${i + 1}/${targets.length}] ${t.candidateID.slice(0, 8)} failed: ${err.message}`);
      }
    }
    pushLog('ok', `Batch done — ${okCount} promoted, ${failCount} failed.`);
    setBatchRunning(false);
    await refresh();
  }

  if (!user) {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc', textAlign: 'center', padding: '80px 24px' }}>
        <h2>Sign in required</h2>
        <p style={{ color: '#94a3b8' }}>The template-candidate review queue is admin-only.</p>
        <Link to="/" style={{ color: '#3b82f6' }}>← Home</Link>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc', textAlign: 'center', padding: '80px 24px' }}>
        <h2>Access denied</h2>
        <p style={{ color: '#94a3b8' }}>This page is restricted to mChatAI admins.</p>
        <Link to="/account" style={{ color: '#3b82f6' }}>← Account</Link>
      </div>
    );
  }

  const visible = candidates.filter(STATUS_FILTERS.find(f => f.id === statusFilter)?.predicate || (() => true));

  return (
    <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc', minHeight: '100vh' }}>
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <h1 style={{ color: '#f8fafc', fontSize: '2rem', fontWeight: 800, margin: 0 }}>Template Candidates</h1>
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
            <Link to="/admin" style={{ color: '#94a3b8', fontSize: '0.85rem' }}>← Admin Dashboard</Link>
            <Link to="/admin/templates" style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Templates Catalog →</Link>
            <Link to="/admin/wisdom" style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Wisdom →</Link>
          </div>
        </div>
        <p style={{ color: '#64748b', marginBottom: 24, fontSize: '0.9rem' }}>
          Review hub-app and user-published candidates for promotion into mchatai-source/harness/templates/.
          Run the discriminator to get an LLM-judged confidence + slug suggestion, then Promote (commits to mchatai-source main)
          or Reject. <strong style={{ color: '#94a3b8' }}>Phase J</strong>.
        </p>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #334155', paddingBottom: 2, flexWrap: 'wrap' }}>
          {STATUS_FILTERS.map(f => {
            const count = candidates.filter(f.predicate).length;
            return (
              <button key={f.id} onClick={() => { setStatusFilter(f.id); setSelected(null); }} style={{
                padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                borderRadius: '8px 8px 0 0',
                background: statusFilter === f.id ? '#1e293b' : 'transparent',
                color: statusFilter === f.id ? '#f8fafc' : '#64748b',
              }}>{f.label} <span style={{ color: '#475569' }}>({count})</span></button>
            );
          })}
          <div style={{ flex: 1 }} />
          <button onClick={refresh} disabled={loading || batchRunning} style={refreshBtnStyle(loading)}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {/* Batch action bar — appears whenever there's a useful batch to run */}
        {(() => {
          const unscreenedCount = candidates.filter(c => !c.discriminator && !c.adminDecision).length;
          const recommendedCount = candidates.filter(c => c.discriminator?.decision === 'promote' && !c.adminDecision).length;
          if (unscreenedCount === 0 && recommendedCount === 0) return null;
          return (
            <div style={{
              display: 'flex', gap: 10, marginBottom: 16, padding: '10px 14px',
              background: '#1e293b', borderRadius: 8, border: '1px solid #334155',
              alignItems: 'center', flexWrap: 'wrap'
            }}>
              <span style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600, marginRight: 4 }}>
                Batch:
              </span>
              {unscreenedCount > 0 && (
                <button
                  onClick={runDiscriminatorAll}
                  disabled={batchRunning}
                  title={`Run discriminator on every candidate that hasn't been screened yet — sequential, ~5-10s each. Will use the policy default provider/model (${policy?.discriminatorProvider || 'openai'} / ${policy?.discriminatorModelByProvider?.[policy?.discriminatorProvider || 'openai'] || 'gpt-4o'}).`}
                  style={batchBtnStyle('#3b82f6', batchRunning)}
                >
                  {batchRunning ? 'Running…' : `Run discrim on all unscreened (${unscreenedCount})`}
                </button>
              )}
              {recommendedCount > 0 && (
                <button
                  onClick={promoteAllRecommended}
                  disabled={batchRunning}
                  title="Promote every candidate the discriminator recommended — sequential commits to mchatai-source main, ~3-5s each. Confirms before starting."
                  style={batchBtnStyle('#22c55e', batchRunning)}
                >
                  {batchRunning ? 'Promoting…' : `Promote all recommended (${recommendedCount})`}
                </button>
              )}
              {batchRunning && (
                <span style={{ color: '#64748b', fontSize: '0.72rem', marginLeft: 'auto' }}>
                  Watch the Recent Actions log below for per-candidate progress.
                </span>
              )}
            </div>
          );
        })()}

        {/* Two-column: list + detail */}
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 20 }}>
          {/* List column */}
          <div>
            {loading ? (
              <div style={{ color: '#94a3b8', padding: 40, textAlign: 'center' }}>Loading…</div>
            ) : visible.length === 0 ? (
              <EmptyState statusFilter={statusFilter} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {visible.map(c => (
                  <CandidateRow
                    key={c.candidateID || c.id}
                    candidate={c}
                    selected={selected?.candidateID === c.candidateID}
                    onSelect={() => setSelected(c)}
                    onDiscriminator={() => runDiscriminator(c)}
                    onPromote={() => promote(c)}
                    onReject={() => reject(c)}
                    busy={busyAction}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Detail panel */}
          {selected && (
            <DetailPanel
              candidate={selected}
              policy={policy}
              existingTemplates={existingTemplates}
              defaultSlugOverride={targetSlugFromURL}
              defaultSubdirOverride={targetSubdirFromURL}
              onClose={() => setSelected(null)}
              onPromote={(override) => promote(selected, override)}
              onReject={() => reject(selected)}
              onDiscriminator={(providerOverride) => runDiscriminator(selected, providerOverride)}
              busy={busyAction}
            />
          )}
        </div>

        {/* Action log */}
        {actionLog.length > 0 && (
          <div style={{ marginTop: 32, padding: 16, background: '#1e293b', borderRadius: 8, border: '1px solid #334155' }}>
            <h3 style={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', margin: '0 0 12px' }}>Recent Actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8rem', fontFamily: 'ui-monospace, monospace' }}>
              {actionLog.map((entry, i) => (
                <div key={i} style={{ color: entry.kind === 'error' ? '#f87171' : '#a3e635' }}>
                  <span style={{ color: '#475569', marginRight: 8 }}>{entry.ts}</span>
                  {entry.message}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Manual seed panel */}
        <div style={{ marginTop: 32, padding: 20, background: '#1e293b', borderRadius: 12, border: '1px solid #334155' }}>
          <h2 style={{ color: '#f8fafc', fontSize: '1.1rem', fontWeight: 700, margin: '0 0 4px' }}>Seed candidates from hub apps</h2>
          <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 0 }}>
            Pull any existing hub app into this queue as a template candidate. Useful for surfacing already-popular apps
            (4★+ ratings, distinct genre) that the catalog is missing. Manually-seeded candidates carry <code>adminSeeded: true</code>.
          </p>

          {/* Manual slug input */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, marginBottom: 24 }}>
            <input
              type="text" placeholder="hub slug (e.g. classic-snake)"
              value={seedSlug} onChange={(e) => setSeedSlug(e.target.value.trim())}
              style={inputStyle}
              onKeyDown={(e) => e.key === 'Enter' && seedSlug && seedFromSlug(seedSlug)}
            />
            <button
              onClick={() => { seedFromSlug(seedSlug); setSeedSlug(''); }}
              disabled={!seedSlug || busyAction?.kind === 'seed'}
              style={primaryBtnStyle(!seedSlug || busyAction?.kind === 'seed')}
            >
              {busyAction?.kind === 'seed' && busyAction.candidateID === seedSlug ? 'Seeding…' : 'Seed slug'}
            </button>
          </div>

          {/* Suggested gaps — grouped by platform so web (miniApp) and
              macOS (macOSApp) sections are visually distinct. Preview link
              points at the right hub URL form per platform. */}
          {(['web', 'macos']).map(platformKey => {
            const platformGaps = SUGGESTED_GAPS.filter(g => g.platform === platformKey);
            if (platformGaps.length === 0) return null;
            const tierAInPlatform = platformGaps.filter(g => g.tier === 'A');
            const platformLabel = platformKey === 'web' ? 'Web (miniApp)' : 'macOS (macOSApp)';
            return (
              <div key={platformKey} style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                  <h3 style={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', margin: 0 }}>
                    Suggested gaps · {platformLabel}
                  </h3>
                  {tierAInPlatform.length > 0 && (
                    <button
                      onClick={() => seedTierA(platformKey)}
                      disabled={seedingAll}
                      style={{ ...primaryBtnStyle(seedingAll), background: '#22c55e', fontSize: '0.78rem' }}
                    >
                      {seedingAll ? 'Seeding…' : `Seed all ${tierAInPlatform.length} Tier A`}
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {platformGaps.map(g => {
                    const alreadySeeded = candidates.some(c => c.publishedURL?.includes(`/${g.slug}/`) || c.publishedURL?.endsWith(`/${g.slug}`));
                    const previewURL = g.platform === 'macos' ? `https://mchatai.com/hub/${g.slug}` : `https://mchatai.com/hub/${g.slug}`;
                    return (
                      <div key={g.slug} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 12px', background: '#0f172a', borderRadius: 6, border: '1px solid #334155'
                      }}>
                        <span style={{
                          fontSize: '0.7rem', fontWeight: 700,
                          background: g.tier === 'A' ? '#3b82f6' : '#64748b',
                          color: '#fff', padding: '2px 6px', borderRadius: 3
                        }}>Tier {g.tier}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: '#f8fafc', fontSize: '0.85rem', fontWeight: 600 }}>{g.label} <span style={{ color: '#64748b', fontWeight: 400 }}>· {g.slug}</span></div>
                          <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{g.why}</div>
                        </div>
                        <a
                          href={previewURL} target="_blank" rel="noopener noreferrer"
                          style={{
                            fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8',
                            textDecoration: 'none', padding: '6px 10px',
                            border: '1px solid #334155', borderRadius: 4
                          }}
                          title={g.platform === 'macos'
                            ? 'Open hub page — click "Download Swift Source" to grab the .zip and run locally before seeding'
                            : 'Open this hub app in a new tab to preview before seeding'}
                        >
                          Preview ↗
                        </a>
                        <button
                          onClick={() => seedFromSlug(g.slug)}
                          disabled={alreadySeeded || (busyAction?.kind === 'seed' && busyAction.candidateID === g.slug)}
                          style={{
                            ...secondaryBtnStyle,
                            opacity: alreadySeeded ? 0.4 : 1,
                            cursor: alreadySeeded ? 'default' : 'pointer'
                          }}
                        >
                          {alreadySeeded ? '✓ Seeded' : busyAction?.kind === 'seed' && busyAction.candidateID === g.slug ? '…' : 'Seed'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

      </section>
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────────────────────

function CandidateRow({ candidate, selected, onSelect, onDiscriminator, onPromote, onReject, busy }) {
  const c = candidate;
  const isBusy = busy?.candidateID === c.candidateID;
  return (
    <div
      onClick={onSelect}
      style={{
        background: selected ? '#1e3a5f' : '#1e293b',
        borderRadius: 8,
        padding: '14px 16px',
        border: `1px solid ${selected ? '#3b82f6' : '#334155'}`,
        cursor: 'pointer',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 12,
        alignItems: 'start'
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: '0.7rem', padding: '2px 6px', borderRadius: 3, fontWeight: 700,
            background: c.adminSeeded ? '#8b5cf6' : c.triggeringSignal === 'publishToCommunity' ? '#22c55e' : '#3b82f6',
            color: '#fff'
          }}>
            {c.adminSeeded ? 'ADMIN-SEED' : c.triggeringSignal}
          </span>
          <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>
            {(c.candidateID || c.id || '').slice(0, 8)}
          </span>
          <span style={{ fontSize: '0.7rem', color: '#475569' }}>
            weight {c.triggerSignalWeight}
          </span>
          {c.discriminator && (
            <DiscriminatorBadge d={c.discriminator} />
          )}
          {c.adminDecision && (
            <DecisionBadge d={c.adminDecision} />
          )}
        </div>
        <div style={{ color: '#f8fafc', fontSize: '0.95rem', fontWeight: 500, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {c.originalGoal || '(no goal)'}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: '#64748b', fontSize: '0.78rem' }}>{c.artifactType}</span>
          {c.publishedURL && (
            <a
              href={c.publishedURL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                fontSize: '0.72rem', fontWeight: 600,
                color: '#fff', background: '#3b82f6',
                padding: '3px 8px', borderRadius: 4,
                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3
              }}
            >
              Open ↗
            </a>
          )}
          {c.publishedURL && (
            <span style={{ color: '#475569', fontSize: '0.72rem', fontFamily: 'ui-monospace, monospace' }}>
              {c.publishedURL.replace(/^https?:\/\//, '').replace(/^mchatai\.com/, '').slice(0, 50)}
            </span>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {!c.adminDecision && (
          <>
            {!c.discriminator && (
              <button onClick={(e) => { e.stopPropagation(); onDiscriminator(); }}
                disabled={isBusy} style={miniBtn('#3b82f6', isBusy)}>
                {isBusy && busy.kind === 'discriminator' ? '…' : 'Run discrim'}
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); onPromote(); }}
              disabled={isBusy} style={miniBtn('#22c55e', isBusy)}>
              {isBusy && busy.kind === 'promote' ? '…' : 'Promote'}
            </button>
            <button onClick={(e) => { e.stopPropagation(); onReject(); }}
              disabled={isBusy} style={miniBtn('#ef4444', isBusy)}>
              {isBusy && busy.kind === 'reject' ? '…' : 'Reject'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function DiscriminatorBadge({ d }) {
  const color = d.decision === 'promote' ? '#22c55e' : '#ef4444';
  return (
    <span style={{ fontSize: '0.7rem', color, fontWeight: 600 }}>
      ▸ {d.decision} ({(d.confidence * 100).toFixed(0)}%)
    </span>
  );
}

function DecisionBadge({ d }) {
  const color = d.kind === 'promoted' ? '#22c55e' : '#ef4444';
  return (
    <span style={{ fontSize: '0.7rem', color, fontWeight: 700, textTransform: 'uppercase' }}>
      ★ {d.kind}
      {d.kind === 'promoted' && d.slug ? ` (${d.slug})` : ''}
    </span>
  );
}

function DetailPanel({ candidate, policy, existingTemplates, defaultSlugOverride, defaultSubdirOverride, onClose, onPromote, onReject, onDiscriminator, busy }) {
  const c = candidate;
  const d = c.discriminator;
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [category, setCategory] = useState('');
  const [keywordsRaw, setKeywordsRaw] = useState('');

  // Per-candidate provider selection. Defaults to the live policy default,
  // falling back to 'openai' if policy hasn't loaded yet (or fetch failed).
  const policyDefaultProvider = (policy?.discriminatorProvider || 'openai').toLowerCase();
  const [selectedProvider, setSelectedProvider] = useState(policyDefaultProvider);
  useEffect(() => {
    // When policy lands later, snap the dropdown to whatever it says — only
    // if the admin hasn't manually picked something different yet.
    setSelectedProvider(policyDefaultProvider);
  }, [policyDefaultProvider]);

  useEffect(() => {
    // Slug priority: URL ?targetSlug param (from "Replace from candidate"
    // link on the Templates catalog page) > discriminator suggestion >
    // empty. The URL param wins so admins coming from a replacement flow
    // don't have to retype the existing slug.
    setSlug(defaultSlugOverride || d?.suggestedSlug || '');
    setDisplayName('');
    setCategory(d?.suggestedCategory || '');
    setKeywordsRaw((d?.matchKeywords || []).join(', '));
  }, [c.candidateID, d?.suggestedSlug, d?.suggestedCategory, d?.matchKeywords, defaultSlugOverride]);

  // Collision detection — flips the Promote button into a "Replace existing"
  // affordance when the slug currently typed matches a template already
  // committed in mchatai-source. Shape: existing manifest (for displayName,
  // sourceURL, extractedAt), or null when no collision.
  const collidingTemplate = (slug && existingTemplates && existingTemplates[slug]) || null;

  const isBusy = busy?.candidateID === c.candidateID;

  function doPromote() {
    if (collidingTemplate) {
      const ok = window.confirm(
        `Replace existing template "${collidingTemplate.slug}"?\n\n` +
        `Existing: ${collidingTemplate.manifest?.displayName || collidingTemplate.slug}` +
        (collidingTemplate.manifest?.extractedAt ? ` (promoted ${collidingTemplate.manifest.extractedAt})` : '') +
        `\n\nThe Cloud Function will commit a NEW reference.html + template-manifest.json over the existing files in mchatai-source main. The previous version stays in git history; the live wizard will use the new content on the next refreshMchataisourceCache.\n\nProceed?`
      );
      if (!ok) return;
    }
    const keywords = keywordsRaw.split(',').map(s => s.trim()).filter(Boolean);
    const override = {};
    if (slug) override.slug = slug;
    if (displayName) override.displayName = displayName;
    if (category) override.category = category;
    if (keywords.length) override.keywords = keywords;
    onPromote(override);
  }

  return (
    <div style={{ background: '#1e293b', borderRadius: 8, padding: 20, border: '1px solid #334155', position: 'sticky', top: 20, alignSelf: 'start', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ color: '#f8fafc', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Candidate detail</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {c.publishedURL && (
            <a
              href={c.publishedURL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '0.78rem', fontWeight: 600,
                color: '#fff', background: '#3b82f6',
                padding: '6px 12px', borderRadius: 6,
                textDecoration: 'none'
              }}
            >
              Open in new tab ↗
            </a>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.4rem', padding: '0 6px' }}>×</button>
        </div>
      </div>

      {/* Live iframe preview — visual verification before promote */}
      {c.publishedURL && (
        <div style={{ marginBottom: 16, background: '#0f172a', borderRadius: 6, border: '1px solid #334155', overflow: 'hidden' }}>
          <div style={{ padding: '6px 10px', background: '#0b1220', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#64748b', fontSize: '0.7rem', fontFamily: 'ui-monospace, monospace' }}>
              Live preview · {c.publishedURL.replace(/^https?:\/\//, '')}
            </span>
            <span style={{ color: '#475569', fontSize: '0.65rem' }}>
              sandbox: scripts only, no top-nav
            </span>
          </div>
          <iframe
            src={c.publishedURL}
            title="Candidate live preview"
            sandbox="allow-scripts allow-same-origin"
            style={{ width: '100%', height: 380, border: 'none', background: '#000', display: 'block' }}
          />
        </div>
      )}

      <KV label="ID" value={c.candidateID} mono />
      <KV label="User UID" value={c.userID} mono />
      <KV label="Trigger" value={`${c.triggeringSignal} (weight ${c.triggerSignalWeight})`} />
      <KV label="Artifact type" value={c.artifactType} />
      <KV label="Published URL" value={c.publishedURL} link />
      <KV label="Original goal" value={c.originalGoal} multiline />

      {d ? (
        <div style={{ marginTop: 16, padding: 12, background: '#0f172a', borderRadius: 6, border: `1px solid ${d.decision === 'promote' ? '#16a34a' : '#dc2626'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <h3 style={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', margin: 0 }}>
              Discriminator — {d.decision} ({(d.confidence * 100).toFixed(0)}%)
            </h3>
            <span style={{ color: '#64748b', fontSize: '0.65rem' }}>
              {d.completedAt ? new Date(d.completedAt).toLocaleString() : ''}
            </span>
          </div>
          <p style={{ color: '#cbd5e1', fontSize: '0.82rem', margin: '6px 0', lineHeight: 1.4 }}>
            {d.reasoning || '(no reasoning recorded)'}
          </p>
          {d.overlapWithExistingTemplates?.length > 0 && (
            <div style={{ marginTop: 8, fontSize: '0.72rem', color: '#94a3b8' }}>
              <span style={{ color: '#64748b' }}>Overlap with existing: </span>
              {d.overlapWithExistingTemplates.join(', ')}
            </div>
          )}
          <div style={{ color: '#64748b', fontSize: '0.7rem', marginTop: 8, fontFamily: 'ui-monospace, monospace' }}>
            {d.provider}/{d.model} · in {d.tokensIn} · out {d.tokensOut}
          </div>
          {!c.adminDecision && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #334155' }}>
              <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginBottom: 6 }}>
                Re-run with a different provider — useful after a prompt tweak or to A/B compare.
              </div>
              <DiscriminatorRunPanel
                policy={policy}
                policyDefaultProvider={policyDefaultProvider}
                selectedProvider={selectedProvider}
                onProviderChange={setSelectedProvider}
                onRun={() => onDiscriminator(selectedProvider === policyDefaultProvider ? null : selectedProvider)}
                isBusy={isBusy}
                busy={busy}
                isRerun
              />
            </div>
          )}
        </div>
      ) : (
        <DiscriminatorRunPanel
          policy={policy}
          policyDefaultProvider={policyDefaultProvider}
          selectedProvider={selectedProvider}
          onProviderChange={setSelectedProvider}
          onRun={() => onDiscriminator(selectedProvider === policyDefaultProvider ? null : selectedProvider)}
          isBusy={isBusy}
          busy={busy}
        />
      )}

      {/* Promote form */}
      {!c.adminDecision && (
        <div style={{ marginTop: 16, padding: 12, background: '#0f172a', borderRadius: 6, border: collidingTemplate ? '1px solid #f59e0b' : '1px solid #334155' }}>
          <h3 style={{ color: collidingTemplate ? '#f59e0b' : '#94a3b8', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', margin: '0 0 8px' }}>
            {collidingTemplate ? 'Replace existing template (commits to mchatai-source)' : 'Promote (commits to mchatai-source)'}
          </h3>

          {/* Collision warning — visible side-by-side with the new candidate's iframe above */}
          {collidingTemplate && (
            <div style={{ background: '#1f1505', border: '1px solid #92400e', borderRadius: 4, padding: '8px 10px', marginBottom: 10 }}>
              <div style={{ color: '#fbbf24', fontSize: '0.75rem', fontWeight: 600, marginBottom: 4 }}>
                ⚠️ This slug is already promoted
              </div>
              <div style={{ color: '#fef3c7', fontSize: '0.78rem', lineHeight: 1.5 }}>
                <strong>{collidingTemplate.manifest?.displayName || collidingTemplate.slug}</strong>
                {collidingTemplate.manifest?.extractedAt && <span style={{ color: '#fde68a' }}> · promoted {collidingTemplate.manifest.extractedAt}</span>}
                {collidingTemplate.manifest?.sourceURL && (
                  <> · <a href={collidingTemplate.manifest.sourceURL} target="_blank" rel="noopener noreferrer" style={{ color: '#fbbf24' }}>existing source ↗</a></>
                )}
                {' · '}
                <a href={collidingTemplate.referenceURL} target="_blank" rel="noopener noreferrer" style={{ color: '#fbbf24' }}>existing reference.html ↗</a>
              </div>
              <div style={{ color: '#fde68a', fontSize: '0.7rem', marginTop: 4 }}>
                Click Replace below to overwrite. Old version stays in git history.
                Type a different slug above to keep both side-by-side.
              </div>
            </div>
          )}

          <Field label="Slug" value={slug} onChange={setSlug} placeholder="kebab-case-slug" />
          <Field label="Display name" value={displayName} onChange={setDisplayName} placeholder="Optional override" />
          <Field label="Category" value={category} onChange={setCategory} placeholder="arcade, board, music…" />
          <Field label="Match keywords (comma-separated)" value={keywordsRaw} onChange={setKeywordsRaw} placeholder="keyword1, keyword2, …" />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={doPromote} disabled={isBusy}
              style={collidingTemplate
                ? { ...primaryBtnStyle(isBusy), background: '#f59e0b' }
                : primaryBtnStyle(isBusy)}>
              {isBusy && busy.kind === 'promote'
                ? (collidingTemplate ? 'Replacing…' : 'Promoting…')
                : (collidingTemplate ? `Replace existing "${collidingTemplate.slug}"` : 'Promote → mchatai-source')}
            </button>
            <button onClick={onReject} disabled={isBusy} style={{ ...primaryBtnStyle(isBusy), background: '#ef4444' }}>
              {isBusy && busy.kind === 'reject' ? 'Rejecting…' : 'Reject'}
            </button>
          </div>
        </div>
      )}

      {/* Already-decided */}
      {c.adminDecision && (
        <div style={{ marginTop: 16, padding: 12, background: '#0f172a', borderRadius: 6, border: '1px solid #334155' }}>
          <h3 style={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', margin: '0 0 8px' }}>
            Decided · {c.adminDecision.kind}
          </h3>
          {c.adminDecision.kind === 'promoted' && (
            <>
              <KV label="Slug" value={c.adminDecision.slug} />
              <KV label="Commit" value={c.adminDecision.commitURL} link />
              <KV label="Paths" value={(c.adminDecision.paths || []).join(', ')} />
            </>
          )}
          {c.adminDecision.kind === 'rejected' && (
            <KV label="Reason" value={c.adminDecision.reason || '(none)'} multiline />
          )}
        </div>
      )}

      {/* Artifact preview */}
      {c.artifactSource && (
        <details style={{ marginTop: 16 }}>
          <summary style={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
            Artifact source ({c.artifactSource.length.toLocaleString()} bytes)
          </summary>
          <pre style={{
            marginTop: 8, padding: 12, background: '#0f172a', borderRadius: 4,
            color: '#cbd5e1', fontSize: '0.7rem', maxHeight: 240, overflow: 'auto',
            fontFamily: 'ui-monospace, monospace'
          }}>
            {c.artifactSource.slice(0, 4000)}
            {c.artifactSource.length > 4000 ? '\n… (truncated)' : ''}
          </pre>
        </details>
      )}
    </div>
  );
}

function DiscriminatorRunPanel({ policy, policyDefaultProvider, selectedProvider, onProviderChange, onRun, isBusy, busy, isRerun = false }) {
  // Build the option list from the policy's discriminatorModelByProvider map
  // intersected with the providers the Cloud Function actually supports.
  // Fallback to a hardcoded shape if policy hasn't loaded — same defaults the
  // Cloud Function uses internally.
  const modelByProvider = policy?.discriminatorModelByProvider || {
    openai: 'gpt-4o-mini',
    anthropic: 'claude-sonnet-4-6',
    google: 'gemini-2.5-flash'
  };
  const orderedProviders = PROVIDER_DISPLAY_ORDER.filter(p => modelByProvider[p]);
  const selectedModel = modelByProvider[selectedProvider];
  const selectedInfo = costInfoFor(selectedProvider, selectedModel);
  const isUnsupported = !SUPPORTED_PROVIDERS.has(selectedProvider);

  return (
    <div style={isRerun
      ? { padding: 0, background: 'transparent', border: 'none' }
      : { marginTop: 16, padding: 14, background: '#0f172a', borderRadius: 6, border: '1px dashed #334155' }}>
      {!isRerun && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>Discriminator hasn't been run yet.</p>
          <span style={{ color: '#475569', fontSize: '0.7rem' }}>
            policy default: <span style={{ color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>{policyDefaultProvider}</span>
          </span>
        </div>
      )}

      <label style={{ color: '#94a3b8', fontSize: '0.72rem', display: 'block', marginBottom: 4 }}>
        Provider
      </label>
      <select
        value={selectedProvider}
        onChange={(e) => onProviderChange(e.target.value)}
        style={{
          width: '100%', padding: '8px 10px', borderRadius: 4,
          border: '1px solid #334155', background: '#1e293b',
          color: '#f8fafc', fontSize: '0.85rem', fontFamily: 'inherit',
          outline: 'none', marginBottom: 8, cursor: 'pointer'
        }}
      >
        {orderedProviders.map(p => {
          const model = modelByProvider[p];
          const info = costInfoFor(p, model);
          const supported = SUPPORTED_PROVIDERS.has(p);
          const labelParts = [
            `${p} · ${model}`,
            info.costPerRun || '',
            !supported ? '(not yet wired)' : (p === policyDefaultProvider ? '(policy default)' : ''),
            info.badge && supported ? `· ${info.badge}` : ''
          ].filter(Boolean);
          return (
            <option key={p} value={p} disabled={!supported}>
              {labelParts.join(' · ')}
            </option>
          );
        })}
      </select>

      {/* Cost guideline + per-provider note */}
      <div style={{
        background: '#1e293b', borderRadius: 4, padding: '8px 10px',
        border: '1px solid #334155', marginBottom: 12,
        fontSize: '0.75rem', color: '#cbd5e1', lineHeight: 1.5
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ color: '#94a3b8', fontWeight: 600 }}>{selectedProvider} · <span style={{ fontFamily: 'ui-monospace, monospace', color: '#cbd5e1' }}>{selectedModel}</span></span>
          <span style={{ color: '#22c55e', fontWeight: 600 }}>{selectedInfo.costPerRun || '~?'}</span>
        </div>
        <div style={{ color: '#64748b', fontSize: '0.72rem', marginBottom: 2 }}>{selectedInfo.perDollar || ''}</div>
        <div style={{ color: '#94a3b8' }}>{selectedInfo.note || ''}</div>
      </div>

      <button
        onClick={onRun}
        disabled={isBusy || isUnsupported}
        style={{ ...primaryBtnStyle(isBusy || isUnsupported), width: '100%' }}
      >
        {isBusy && busy.kind === 'discriminator'
          ? (isRerun ? 'Re-running…' : 'Running…')
          : isUnsupported
            ? `${selectedProvider} not yet wired in runDiscriminator`
            : `${isRerun ? 'Re-run' : 'Run'} discriminator (${selectedProvider})`}
      </button>
    </div>
  );
}

function KV({ label, value, mono = false, link = false, multiline = false }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: '0.82rem', alignItems: multiline ? 'start' : 'baseline' }}>
      <span style={{ color: '#64748b', flex: '0 0 110px' }}>{label}</span>
      {link ? (
        <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', wordBreak: 'break-all' }}>{value}</a>
      ) : (
        <span style={{
          color: '#cbd5e1',
          fontFamily: mono ? 'ui-monospace, monospace' : 'inherit',
          fontSize: mono ? '0.78rem' : 'inherit',
          flex: 1, minWidth: 0,
          wordBreak: 'break-word',
          whiteSpace: multiline ? 'pre-wrap' : 'normal'
        }}>{value}</span>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.72rem', display: 'block', marginBottom: 2 }}>{label}</label>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        style={{
          width: '100%', padding: '6px 10px', borderRadius: 4, border: '1px solid #334155',
          background: '#0f172a', color: '#f8fafc', fontSize: '0.82rem', fontFamily: 'inherit', outline: 'none'
        }}
      />
    </div>
  );
}

function EmptyState({ statusFilter }) {
  const messages = {
    unscreened: 'No unscreened candidates. Manually seed one below or wait for organic Publish-to-Hub captures.',
    recommended: 'No discriminator-recommended candidates yet.',
    'declined-by-discriminator': 'No discriminator-declined candidates.',
    promoted: 'No templates have been promoted yet.',
    rejected: 'No candidates have been rejected yet.',
    all: 'No candidates in the queue. Manually seed one below.'
  };
  return (
    <div style={{ padding: 40, textAlign: 'center', background: '#1e293b', borderRadius: 8, border: '1px dashed #334155' }}>
      <p style={{ color: '#94a3b8', margin: 0 }}>{messages[statusFilter] || 'Empty.'}</p>
    </div>
  );
}

// ── Style helpers ────────────────────────────────────────────────────────────

const inputStyle = {
  flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #334155',
  background: '#0f172a', color: '#f8fafc', fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none'
};

function primaryBtnStyle(disabled) {
  return {
    background: '#3b82f6', color: '#fff', border: 'none', padding: '10px 16px',
    borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit', opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap'
  };
}

const secondaryBtnStyle = {
  background: '#334155', color: '#f8fafc', border: 'none', padding: '6px 12px',
  borderRadius: 4, fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit',
  whiteSpace: 'nowrap'
};

function refreshBtnStyle(loading) {
  return {
    background: '#1e293b', color: '#94a3b8', border: '1px solid #334155',
    padding: '6px 14px', borderRadius: 4, fontWeight: 500, fontSize: '0.78rem',
    cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: loading ? 0.5 : 1
  };
}

function miniBtn(color, disabled) {
  return {
    background: color, color: '#fff', border: 'none', padding: '4px 10px',
    borderRadius: 4, fontWeight: 600, fontSize: '0.72rem', cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit', opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap', minWidth: 80
  };
}

function batchBtnStyle(color, disabled) {
  return {
    background: color, color: '#fff', border: 'none',
    padding: '8px 14px', borderRadius: 6,
    fontSize: '0.8rem', fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit', opacity: disabled ? 0.5 : 1,
    whiteSpace: 'nowrap'
  };
}
